import { create } from 'zustand'

/**
 * Tracks which text layers currently overflow the canvas (their content is taller than
 * the frame, so the published master — which clips to the canvas — would CUT THEM OFF).
 * The editing canvas measures this and publishes the set; the floating toolbar reads it
 * to warn the user before they ship a clipped asset. Read-only previews never write here
 * (the measurement callback is editor-only), so this stays scoped to the live editor.
 */
interface LayerOverflowStore {
  /** Layer ids whose rendered text content clips the canvas frame. Read reactively via
   *  `useLayerOverflowStore(s => s.overflowing.has(id))` so consumers re-render on change. */
  overflowing: Set<string>
  setOverflowing: (ids: string[]) => void
}

function sameSet(a: Set<string>, b: string[]): boolean {
  if (a.size !== b.length) return false
  for (const id of b) if (!a.has(id)) return false
  return true
}

export const useLayerOverflowStore = create<LayerOverflowStore>((set, get) => ({
  overflowing: new Set(),
  setOverflowing: ids => {
    // Skip the state write (and re-render) when nothing actually changed — the canvas
    // re-measures on every layout pass, so most calls are no-ops.
    if (sameSet(get().overflowing, ids)) return
    set({ overflowing: new Set(ids) })
  },
}))
